import { jsx, jsxs } from "react/jsx-runtime";
import { Button, DatePicker, TRANSFORMER_MAX_MOBILE_WIDTH_PX, TravelerSelection } from "@pcln/horizon";
import { addMonths } from "date-fns/addMonths";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useMediaQuery } from "react-responsive";
import { BundleAndSave } from "../shared/components/BundleAndSave.js";
import { SearchFormGrid } from "../shared/components/SearchFormGrid.js";
import { SubmitButton } from "../shared/components/SubmitButton.js";
import { DEFAULT_COLLISION_AVOIDANCE } from "../shared/constants.js";
import { useContainerBreakpoint } from "../shared/hooks/useContainerBreakpoint.js";
import { useTabGroup } from "../shared/hooks/useTabGroup.js";
import { createYupResolver } from "../shared/validation/yup-form-resolver.js";
import { HOTEL_LABELS } from "./constants.js";
import { DEFAULT_HOTEL_FORM_STATE, STAY_SEARCH_CALENDAR_MONTHS, getHotelTravelerFieldErrorMessage, useHotelDateRangeCalendar } from "./hotelFormShared.js";
import { getBackButtonClass, getFormContainerClass, getGridContainerClass, getInputClass, getWideLayoutBreakpointPx } from "./HotelSearchForm.variants.js";
import validation, { INFANT_CAPACITY_LIMIT, isInfantLimitReachedPackages } from "./validation.js";
function HotelSearchForm({ onSubmit, initialValues, isMobile = false, renderAutocomplete, setLocation, showBundleAndSave = true, removeBackground = false, onBundleChange, onFormValuesChange, onItemClick, autoAdvance = false, typeAheadEndpoint = 'hotels', hotelPlannerLinkHref, onMaxRoomsClick, renderSelectedFilterChipBar, isStickyInEffect = false, collisionAvoidance = DEFAULT_COLLISION_AVOIDANCE, disabledDates, submitLabel = 'Find Your Hotel', submitButtonType = 'primary', submitButtonEmphasis, submitButtonFixedOnMobile, singleRow = false, showCurrentLocation = true, onBackButtonClick, backButtonLabel = HOTEL_LABELS.backButton }) {
    const shouldFixSubmitButtonOnMobile = submitButtonFixedOnMobile ?? isMobile;
    const mergedInitialValues = useMemo(()=>({
            ...DEFAULT_HOTEL_FORM_STATE,
            ...initialValues
        }), [
        initialValues
    ]);
    const { registerTabElement, focusNextElement } = useTabGroup();
    const [addFlights, setAddFlights] = useState(false);
    const isMobileComponent = useMediaQuery({
        maxWidth: TRANSFORMER_MAX_MOBILE_WIDTH_PX
    });
    const { clearErrors, control, setValue, handleSubmit, watch, trigger, formState: { errors } } = useForm({
        defaultValues: mergedInitialValues,
        mode: 'onSubmit',
        resolver: createYupResolver(validation),
        reValidateMode: 'onChange'
    });
    const watchTripType = useWatch({
        control,
        name: 'tripType',
        defaultValue: mergedInitialValues.tripType
    });
    const watchStartLocation = useWatch({
        control,
        name: 'startLocation',
        defaultValue: mergedInitialValues.startLocation
    });
    const watchEndLocation = useWatch({
        control,
        name: 'endLocation',
        defaultValue: mergedInitialValues.endLocation
    });
    const watchStartDate = useWatch({
        control,
        name: 'startDate',
        defaultValue: mergedInitialValues.startDate
    });
    const watchEndDate = useWatch({
        control,
        name: 'endDate',
        defaultValue: mergedInitialValues.endDate
    });
    const watchTravelers = useWatch({
        control,
        name: 'travelers',
        defaultValue: mergedInitialValues.travelers
    });
    useEffect(()=>{
        const subscription = watch((value)=>{
            if (onFormValuesChange) onFormValuesChange(value);
        });
        return ()=>subscription.unsubscribe();
    }, [
        watch,
        onFormValuesChange
    ]);
    useEffect(()=>{
        if (setLocation && watchEndLocation) setLocation(watchEndLocation);
    }, [
        watchEndLocation,
        setLocation
    ]);
    useEffect(()=>{
        clearErrors('startLocation');
    }, [
        clearErrors,
        watchStartLocation
    ]);
    useEffect(()=>{
        clearErrors('endLocation');
    }, [
        clearErrors,
        watchEndLocation
    ]);
    useEffect(()=>{
        clearErrors('startDate');
    }, [
        clearErrors,
        watchStartDate,
        watchTripType
    ]);
    useEffect(()=>{
        clearErrors('endDate');
    }, [
        clearErrors,
        watchEndDate,
        watchTripType
    ]);
    useEffect(()=>{
        if (watchTravelers) {
            trigger('travelers.children');
            trigger('travelers.rooms');
        }
    }, [
        trigger,
        watchTripType,
        watchTravelers
    ]);
    const [bundleSelections, setBundleSelections] = useState({
        flights: false,
        car: false
    });
    const handleBundleSelectionChange = useCallback((updatedTripType, checked, optionValue)=>{
        setBundleSelections((prev)=>({
                ...prev,
                [optionValue]: checked
            }));
        setValue('tripType', updatedTripType);
        onBundleChange?.(updatedTripType, checked, optionValue);
        if ('FLY' === optionValue) setAddFlights(checked);
    }, [
        setValue,
        onBundleChange
    ]);
    const { currentDate, defaultMonth, initialSelectedDates, maxSelectableDate, onCalendarChange } = useHotelDateRangeCalendar({
        setValue,
        startDate: mergedInitialValues.startDate,
        endDate: mergedInitialValues.endDate
    });
    const [selectedFilterChipData, setSelectedFilterChipData] = useState(()=>({
            amenities: new Set(mergedInitialValues.selectedTopFilters?.amenities),
            hotelStarLevel: new Set(mergedInitialValues.selectedTopFilters?.hotelStarLevel),
            guestRating: new Set(mergedInitialValues.selectedTopFilters?.guestRating),
            propertyTypes: new Set(mergedInitialValues.selectedTopFilters?.propertyTypes)
        }));
    const [geolocationError, setGeolocationError] = useState(null);
    const onItemSelect = useCallback((item, searchKey)=>{
        setValue(searchKey, item);
        if ('endLocation' === searchKey && item) setGeolocationError(null);
        if (isMobile || !item) {
            if (isMobile && !!item && document.activeElement instanceof HTMLElement) document.activeElement.blur();
        } else {
            onItemClick?.();
            if (autoAdvance) focusNextElement(true);
        }
    }, [
        isMobile,
        setValue,
        onItemClick,
        autoAdvance,
        focusNextElement
    ]);
    const hasSelectedFilters = Object.values(selectedFilterChipData).some((value)=>value.size > 0);
    const showSelectedFilters = 'STAY' === watchTripType && hasSelectedFilters;
    const handleTravelerChange = (travelerInfo)=>{
        setValue('travelers', travelerInfo);
    };
    const handleSubmitHelper = handleSubmit((data)=>{
        onSubmit({
            ...data,
            mapView: false,
            selectedTopFilters: selectedFilterChipData
        });
    });
    const numberOfMonthsDisplay = 'STAY' === watchTripType ? STAY_SEARCH_CALENDAR_MONTHS : 12;
    const hasBackButton = !!onBackButtonClick;
    const formClasses = getFormContainerClass(addFlights, isStickyInEffect, showSelectedFilters);
    const containerRef = useRef(null);
    const isWideLayout = useContainerBreakpoint(containerRef, getWideLayoutBreakpointPx(addFlights, isStickyInEffect, showSelectedFilters, singleRow));
    const gridContainerClass = getGridContainerClass(addFlights, isStickyInEffect, showSelectedFilters, singleRow, hasBackButton);
    const packageDealsSection = /*#__PURE__*/ jsx("div", {
        className: getInputClass('packageDeals', isStickyInEffect),
        children: showBundleAndSave && /*#__PURE__*/ jsx(BundleAndSave, {
            checkedValues: bundleSelections,
            onSelectionChange: handleBundleSelectionChange,
            tripType: watchTripType,
            productType: "hotels",
            removeBackground: removeBackground
        })
    });
    return /*#__PURE__*/ jsxs(SearchFormGrid, {
        gridClassName: gridContainerClass,
        ariaLabel: "horizon-search-form",
        onSubmit: handleSubmitHelper,
        containerRef: containerRef,
        containerClassName: formClasses,
        formClassName: isStickyInEffect ? 'w-full min-w-0' : void 0,
        innerClassName: isStickyInEffect ? 'w-full min-w-0' : void 0,
        children: [
            hasBackButton && /*#__PURE__*/ jsx(Button, {
                buttonType: "button",
                type: "tertiary",
                className: getBackButtonClass(addFlights, isStickyInEffect, showSelectedFilters, singleRow),
                iconLeft: "chevron_left",
                onClick: onBackButtonClick,
                children: backButtonLabel
            }),
            addFlights && /*#__PURE__*/ jsx("div", {
                className: getInputClass('flight'),
                children: renderAutocomplete?.({
                    searchKey: 'startLocation',
                    label: HOTEL_LABELS.startLocation,
                    typeAheadEndpoint: 'packagesOrigin',
                    errorMessage: errors.startLocation?.message,
                    initialSelectedItem: watchStartLocation,
                    onItemSelect: (item)=>onItemSelect(item, 'startLocation'),
                    inputRef: autoAdvance ? registerTabElement : void 0
                })
            }),
            /*#__PURE__*/ jsx("div", {
                className: getInputClass('location'),
                children: renderAutocomplete?.({
                    searchKey: 'endLocation',
                    label: HOTEL_LABELS.endLocation,
                    typeAheadEndpoint: typeAheadEndpoint,
                    errorMessage: errors.endLocation?.message ?? geolocationError ?? void 0,
                    initialSelectedItem: watchEndLocation,
                    onItemSelect: (item)=>onItemSelect(item, 'endLocation'),
                    inputRef: autoAdvance ? registerTabElement : void 0,
                    showCurrentLocation,
                    onGeolocationError: setGeolocationError,
                    selectedFilterChipData,
                    setSelectedFilterChipData,
                    showFilterChips: 'STAY' === watchTripType
                })
            }),
            /*#__PURE__*/ jsx("div", {
                className: getInputClass('datePicker'),
                children: /*#__PURE__*/ jsx(DatePicker, {
                    errorMessage: errors.startDate?.message || errors.endDate?.message,
                    className: "w-full",
                    title: "Hotels: Check-in - Check-out",
                    label: HOTEL_LABELS.datePicker,
                    mode: "range",
                    numberOfMonths: isMobileComponent ? numberOfMonthsDisplay : 2,
                    displayNights: true,
                    handleCalendarChange: onCalendarChange,
                    initialSelected: initialSelectedDates,
                    disabled: {
                        before: disabledDates?.before || currentDate,
                        after: disabledDates?.after || maxSelectableDate
                    },
                    startMonth: disabledDates?.before || currentDate,
                    endMonth: addMonths(currentDate, numberOfMonthsDisplay - 1),
                    defaultMonth: defaultMonth,
                    min: 1,
                    inputRef: autoAdvance ? registerTabElement : void 0,
                    collisionAvoidance: collisionAvoidance
                })
            }),
            /*#__PURE__*/ jsx("div", {
                className: getInputClass('travelers'),
                children: /*#__PURE__*/ jsx(TravelerSelection, {
                    isHotel: !watchTripType.includes('FLY'),
                    initialValues: mergedInitialValues.travelers,
                    roomsEnabled: true,
                    maxRooms: 'STAY' === watchTripType ? 9 : 4,
                    maxChildren: 'STAY' === watchTripType ? 8 : void 0,
                    maxTravelers: 'STAY' === watchTripType ? 36 : 8,
                    fireMaxRoomsOnClick: onMaxRoomsClick,
                    ageSelectEnabled: true,
                    tooltipErrorMessageId: "travelerSelectionError",
                    errorMessage: getHotelTravelerFieldErrorMessage(errors.travelers) || isInfantLimitReachedPackages(watchTravelers?.adults, watchTravelers?.childrenAges, watchTripType) && INFANT_CAPACITY_LIMIT || '',
                    onChange: (values)=>handleTravelerChange(values),
                    collisionAvoidance: collisionAvoidance,
                    ...hotelPlannerLinkHref && {
                        maxRoomsLinkAttrs: {
                            href: hotelPlannerLinkHref,
                            target: '_blank'
                        }
                    }
                })
            }),
            showSelectedFilters && renderSelectedFilterChipBar && /*#__PURE__*/ jsx("div", {
                className: getInputClass('selectedFilters'),
                children: renderSelectedFilterChipBar({
                    selectedFilterChipData,
                    setSelectedFilterChipData
                })
            }),
            !isWideLayout && packageDealsSection,
            /*#__PURE__*/ jsx(SubmitButton, {
                className: getInputClass('submit'),
                isMobile: isMobile && shouldFixSubmitButtonOnMobile,
                type: submitButtonType,
                emphasis: submitButtonEmphasis,
                children: submitLabel
            }),
            isWideLayout && packageDealsSection
        ]
    });
}
export { HotelSearchForm };
